import { inject, Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { AbstractControl } from '@angular/forms';

/**
 * An unsaved changes guard which checks registered forms for dirty state
 * before allowing navigation away from a component.
 */
@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesGuard implements CanDeactivate<unknown> {

  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly registeredForms = new Set<AbstractControl>();

  // registerForms to be checked when trying to navigate away
  registerForms(forms: AbstractControl[]): void {
    forms?.forEach(form => this.registeredForms.add(form));
  }

  clearForms(): void {
    this.registeredForms.clear();
  }

  canDeactivate(): Observable<boolean> {
    const hasDirtyForms = Array.from(this.registeredForms).some(form => form.dirty);

    if (hasDirtyForms) {
      return this.openUnsavedChangesModal().pipe(
        map((canNavigate) => {
          if (canNavigate) {
            this.clearForms();
          }
          return canNavigate;
        })
      );
    }

    this.clearForms();
    return of(true);
  }

  private openUnsavedChangesModal(): Observable<boolean> {
    return this.confirmationModal
      .openModal({
        type: 'warn',
        title: 'MEETING_SCHEDULER.UNSAVED_CHANGES.TITLE',
        message: 'MEETING_SCHEDULER.UNSAVED_CHANGES.MESSAGE',
        confirmButtonText: 'MEETING_SCHEDULER.UNSAVED_CHANGES.CONFIRM',
      })
      .pipe(map((confirmation) => !!confirmation));
  }
}
